import { useAppSelector, useAppDispatch } from '@common-reducers/hooks/store.hook';
import { DBUserMessageThunks } from '@common-reducers/DBServiceThunks';
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { selectFilteredUserMessages } from '@common-reducers/CampaignSelectors';
import { useState, useEffect } from 'react';
import { MessageTable } from './components/MessageTable';
import { MessageCard } from './components/MessageCard';
import { MessageFilters } from './components/MessageFilters';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { LayoutGrid, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { MediaPreviewDialog } from './components/MediaPreview';
import { Button } from '@/components/ui/button';

interface MessagesTabProps {
    supportedUserId: string | null;
}

export function MessagesTab({ supportedUserId }: MessagesTabProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState<UserMessage | null>(null);
    const [viewMode, setViewMode] = useState<'timeline' | 'list'>('list');
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    
    // Filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string | 'all'>('all');
    
    const dispatch = useAppDispatch();

    // Get total count from reducer
    const totalMessages = useAppSelector(state => state.DBUserMessageReducer.total || 0);
    const totalPages = Math.ceil(totalMessages / pageSize);

    // Load messages for the user with pagination
    useEffect(() => {
        if (supportedUserId) {
            setIsLoading(true);
            const query: any = {
                owner: supportedUserId,
                $sort: { dueDate: -1 },
                $skip: (currentPage - 1) * pageSize,
                $limit: pageSize
            };
            
            // Apply status filter on server side if not 'all'
            if (statusFilter !== 'all') {
                query.status = statusFilter;
            }
            
            dispatch(DBUserMessageThunks.find(query))
                .finally(() => setIsLoading(false));
        }
    }, [supportedUserId, dispatch, currentPage, pageSize, statusFilter]);

    // Get filtered messages using selector
    const filteredMessages = useAppSelector(state => 
        supportedUserId 
            ? selectFilteredUserMessages(state, supportedUserId, searchQuery, statusFilter)
            : []
    );

    // Whether the messages fetch has completed. This flips to true once the find
    // resolves regardless of how many messages came back, so users with zero
    // messages no longer get stuck on the loading spinner forever.
    const itemsLoaded = useAppSelector(state => state.DBUserMessageReducer.itemsLoaded);

    const handleClearFilters = () => {
        setSearchQuery('');
        setStatusFilter('all');
        setCurrentPage(1);
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(1); // Reset to first page when changing page size
    };

    const handleMediaClick = (message: UserMessage) => {
        setSelectedMedia(message);
    };

    if (!supportedUserId) {
        return (
            <div className="flex items-center justify-center py-12 text-slate-500">
                No user selected
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full gap-4">
            {/* Filters */}
            <MessageFilters
                searchQuery={searchQuery}
                statusFilter={statusFilter}
                onSearchChange={setSearchQuery}
                onStatusFilterChange={setStatusFilter}
                onClearFilters={handleClearFilters}
            />

            {/* View Toggle */}
            <div className="flex items-center justify-between">
                <div className="text-sm text-slate-600">
                    Showing {filteredMessages.length} message{filteredMessages.length !== 1 ? 's' : ''} 
                    {totalMessages > 0 && ` (${totalMessages} total)`}
                </div>
                <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'timeline' | 'list')}>
                    <TabsList>
                        <TabsTrigger value="timeline">
                            <LayoutGrid className="h-4 w-4 mr-2" />
                            Timeline
                        </TabsTrigger>
                        <TabsTrigger value="list">
                            <List className="h-4 w-4 mr-2" />
                            List
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto">
                {!itemsLoaded || isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600 mb-4"></div>
                            <div className="text-slate-500">Loading messages...</div>
                        </div>
                    </div>
                ) : viewMode === 'timeline' ? (
                    <div className="space-y-4">
                        {filteredMessages.length === 0 ? (
                            <div className="text-center py-12 text-slate-500">
                                No messages found matching your filters
                            </div>
                        ) : (
                            filteredMessages.map((message) => (
                                <MessageCard
                                    key={message._id}
                                    message={message}
                                    onMediaClick={handleMediaClick}
                                />
                            ))
                        )}
                    </div>
                ) : (
                    <MessageTable
                        messages={filteredMessages}
                        onMediaClick={handleMediaClick}
                    />
                )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between border-t pt-4">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-500">Rows per page:</span>
                        <select
                            value={pageSize}
                            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                            className="px-2 py-1 border rounded text-sm bg-white dark:bg-slate-800"
                        >
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                        </select>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-sm text-slate-600">
                            Page {currentPage} of {totalPages} ({totalMessages} total)
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1 || isLoading}
                                className="h-8 w-8 p-0"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage >= totalPages || isLoading}
                                className="h-8 w-8 p-0"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <MediaPreviewDialog 
                open={!!selectedMedia} 
                onClose={() => setSelectedMedia(null)} 
                message={selectedMedia} 
            />
        </div>
    );
} 